import { useParams, Navigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Breadcrumb from "@/components/Breadcrumb";
import SEOHead from "@/components/SEOHead";
import TreatmentForm from "@/components/TreatmentForm";
import { Button } from "@/components/ui/button";
import { Check, MessageCircle, Gift } from "lucide-react";
import { getTreatmentBySlug, treatments } from "@/data/treatments";

const TreatmentPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const treatment = slug ? getTreatmentBySlug(slug) : undefined;

  if (!treatment) {
    return <Navigate to="/trattamenti" replace />;
  }

  const categoryLabel =
    treatment.category === "ayurvedico" ? "Trattamento Ayurvedico" : "Trattamento Olistico";

  const [lead, ...rest] = treatment.body;

  const related = treatments
    .filter((t) => t.slug !== treatment.slug && t.category === treatment.category)
    .slice(0, 3);

  const waHref = `https://wa.me/393461533000?text=${encodeURIComponent(
    `Ciao! Vorrei informazioni sul trattamento: ${treatment.name}`,
  )}`;

  return (
    <Layout>
      <SEOHead
        title={`${treatment.name} | Studio Olistico ORIGINI`}
        description={treatment.short}
        image={treatment.image}
        url={`/trattamenti/${treatment.slug}`}
        serviceName={treatment.name}
        serviceDescription={treatment.short}
      />

      {/* Header testuale — sempre nitido e leggibile */}
      <header className="bg-secondary/40 pt-28 pb-12">
        <div className="container mx-auto px-4">
          <Breadcrumb
            items={[
              { label: "Trattamenti", href: "/trattamenti" },
              { label: treatment.name },
            ]}
          />
          <div className="max-w-3xl mt-4">
            <p className="eyebrow">{categoryLabel}</p>
            <h1 className="mt-3 font-serif text-5xl md:text-7xl text-primary leading-tight">
              {treatment.name}
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-light text-foreground/80 leading-relaxed">
              {treatment.short}
            </p>
          </div>
        </div>
      </header>

      {/* Immagine incorniciata (contenuta, non stirata) */}
      <div className="container mx-auto px-4 -mt-2">
        <div className="max-w-4xl mx-auto">
          <div className="aspect-[3/2] w-full overflow-hidden rounded-3xl shadow-xl ring-1 ring-border">
            <img
              src={treatment.image}
              alt={treatment.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* Corpo + sidebar prenotazione */}
      <div className="container mx-auto px-4 py-14">
        <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
          {/* Testo */}
          <article className="lg:col-span-2">
            <p className="text-2xl font-serif text-foreground leading-relaxed mb-6">
              {lead}
            </p>
            <div className="space-y-5">
              {rest.map((paragraph, i) => (
                <p key={i} className="text-lg leading-relaxed text-foreground/90">
                  {paragraph}
                </p>
              ))}
            </div>

            {treatment.benefits.length > 0 && (
              <div className="mt-12">
                <h2 className="font-serif text-3xl md:text-4xl text-primary mb-6">
                  Benefici
                </h2>
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                  {treatment.benefits.map((benefit, i) => (
                    <li key={i} className="flex gap-3 items-start">
                      <span className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full bg-accent/15 flex items-center justify-center">
                        <Check className="w-3.5 h-3.5 text-accent" />
                      </span>
                      <span className="text-foreground/90">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {treatment.note && (
              <div className="mt-10 flex gap-4 items-start bg-accent/10 rounded-2xl p-6">
                <Gift className="w-6 h-6 text-accent flex-shrink-0 mt-0.5" />
                <p className="text-foreground/90 italic">{treatment.note}</p>
              </div>
            )}
          </article>

          {/* Sidebar CTA sticky */}
          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-card border border-border rounded-3xl p-8 shadow-lg">
              <h3 className="font-serif text-2xl text-primary mb-2">
                Prenota questo trattamento
              </h3>
              <p className="text-muted-foreground text-sm mb-6">
                Ricevo solo su appuntamento. Scrivimi su WhatsApp per informazioni e
                disponibilità: ti rispondo personalmente.
              </p>
              <Button
                asChild
                size="lg"
                className="w-full rounded-full bg-[#25D366] hover:bg-[#1eb355] text-white"
              >
                <a href={waHref} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Scrivi su WhatsApp
                </a>
              </Button>
              <a
                href="#richiedi"
                className="block text-center text-sm text-primary hover:text-accent mt-4 transition-colors"
              >
                oppure compila il form ↓
              </a>
            </div>
          </aside>
        </div>
      </div>

      {/* Form */}
      <section id="richiedi" className="bg-secondary/30 py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <TreatmentForm preselectedTreatment={treatment.name} />
        </div>
      </section>

      {/* Altri trattamenti */}
      {related.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="font-serif text-3xl md:text-4xl text-primary text-center mb-10">
              Altri trattamenti
            </h2>
            <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {related.map((t) => (
                <Link key={t.slug} to={`/trattamenti/${t.slug}`} className="group">
                  <div className="overflow-hidden rounded-2xl shadow-md">
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={t.image}
                        alt={t.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5 bg-card">
                      <h3 className="font-serif text-xl text-primary group-hover:text-accent transition-colors">
                        {t.name}
                      </h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default TreatmentPage;
